// prop-types linter doesn't like the airbnb-prop-types wrapper:
/* eslint react/prop-types:"off" */

import React from 'react';
import PropTypes from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';
import { forbidExtraProps, nonNegativeInteger } from 'airbnb-prop-types';
import moment from 'moment';

import isTouchDevice from 'is-touch-device';

import isInclusivelyAfterDay from 'react-dates/lib/utils/isInclusivelyAfterDay';

import FocusedInputShape from 'react-dates/lib/shapes/FocusedInputShape';
import ScrollableOrientationShape from 'react-dates/lib/shapes/ScrollableOrientationShape';

import { isSameMonth, isNextMonth, toStartOfMonth, toEndOfMonth } from './month';

import {
  START_DATE,
  END_DATE,
  HORIZONTAL_ORIENTATION,
} from 'react-dates/constants';

import MonthPicker from "./MonthPicker";

const propTypes = forbidExtraProps({
  startDate: momentPropTypes.momentObj,
  endDate: momentPropTypes.momentObj,
  onDatesChange: PropTypes.func,

  today: momentPropTypes.momentObj,

  focusedInput: FocusedInputShape,
  onFocusChange: PropTypes.func,
  onClose: PropTypes.func,

  keepOpenOnDateSelect: PropTypes.bool,
  minimumNights: PropTypes.number,
  isOutsideRange: PropTypes.func,
  isDayBlocked: PropTypes.func,
  isDayHighlighted: PropTypes.func,

  // DayPicker props
  enableOutsideDays: PropTypes.bool,
  numberOfYears: PropTypes.number,
  orientation: ScrollableOrientationShape,
  withPortal: PropTypes.bool,
  initialVisibleYear: PropTypes.func,
  hideKeyboardShortcutsPanel: PropTypes.bool,
  monthSize: nonNegativeInteger,

  navPrev: PropTypes.node,
  navNext: PropTypes.node,

  onPrevClick: PropTypes.func,
  onNextClick: PropTypes.func,
  onOutsideClick: PropTypes.func,
  renderMonth: PropTypes.func,
  renderCalendarInfo: PropTypes.func,

  // accessibility
  onBlur: PropTypes.func,
  isFocused: PropTypes.bool,
  showKeyboardShortcuts: PropTypes.bool,

  // i18n
  monthFormat: PropTypes.string,

  isRTL: PropTypes.bool
});

const defaultProps = {
  startDate: void 0, // TODO: use null
  endDate: void 0, // TODO: use null
  onDatesChange() {},

  focusedInput: null,
  onFocusChange() {},

  keepOpenOnDateSelect: false,
  minimumNights: 0, // allow start and end month to be the same

  isOutsideRange() {},
  isDayBlocked() {},
  isDayHighlighted() {},

  // DayPicker props
  renderMonth: null,
  renderCalendarInfo: null,

  enableOutsideDays: false,
  numberOfYears: 1,
  orientation: HORIZONTAL_ORIENTATION,
  withPortal: false,

  initialVisibleYear: () => moment(),
  hideKeyboardShortcutsPanel: false,

  navPrev: null,
  navNext: null,

  onPrevClick() {},
  onNextClick() {},
  onOutsideClick() {},

  // accessibility
  onBlur() {},
  isFocused: false,
  showKeyboardShortcuts: false,

  // i18n
  monthFormat: 'MMMM YYYY',

  isRTL: false
};

export default class MonthPickerRangeController extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hoverDate: null,
    };

    this.isTouchDevice = isTouchDevice();
    this.onMonthClick = this.onMonthClick.bind(this);
    this.onMonthMouseEnter = this.onMonthMouseEnter.bind(this);
    this.onMonthMouseLeave = this.onMonthMouseLeave.bind(this);
  }

  get today() {
    return this.props.today || moment();
  }

  onMonthClick(day, options, e) {
    const { keepOpenOnDateSelect, minimumNights } = this.props;
    if (e) e.preventDefault();
    if (this.isBlocked(day)) return;

    const { focusedInput } = this.props;
    let { startDate, endDate } = this.props;

    if (focusedInput === START_DATE) {
      this.props.onFocusChange(END_DATE);

      startDate = day;

      if (isInclusivelyAfterDay(day, endDate)) {
        endDate = null;
      }
    } else if (focusedInput === END_DATE) {
      const firstAllowedEndDate = startDate && startDate.clone().add(minimumNights, 'days');

      if (!startDate) {
        endDate = toEndOfMonth(day);
        this.props.onFocusChange(START_DATE);
      } else if (isInclusivelyAfterDay(day, firstAllowedEndDate)) {
        endDate = toEndOfMonth(day);
        if (!keepOpenOnDateSelect) this.props.onFocusChange(null);
      } else {
        startDate = toStartOfMonth(day);
        endDate = null;
      }
    }

    this.props.onDatesChange({ startDate, endDate });
    this.props.onBlur();
  }

  onMonthMouseEnter(day) {
    if (this.isTouchDevice) return;

    this.setState({
      hoverDate: day,
    });
  }

  onMonthMouseLeave() {
    if (this.isTouchDevice) return;

    this.setState({
      hoverDate: null,
    });
  }

  doesNotMeetMinimumNights(day) {
    const { startDate, isOutsideRange, focusedInput, minimumNights } = this.props;
    if (focusedInput !== END_DATE) return false;

    if (startDate) {
      const dayDiff = day.diff(startDate.clone().startOf('day').hour(12), 'days');
      return dayDiff < minimumNights && dayDiff >= 0;
    }
    return isOutsideRange(moment(day).subtract(minimumNights, 'days'));
  }

  isMonthAfterHoveredStartDate(month) {
    const { startDate, endDate, minimumNights } = this.props;
    const { hoverDate } = this.state;
    return !!startDate && !endDate && !this.isBlocked(month) && isNextMonth(hoverDate, month) &&
      minimumNights > 0 && isSameMonth(hoverDate, startDate);
  }

  isHovered(day) {
    return isSameMonth(day, this.state.hoverDate);
  }

  isInHoveredSpan(day) {
    const { startDate, endDate } = this.props;
    const { hoverDate } = this.state;

    const isForwardRange = !!startDate && !endDate &&
      (day.isBetween(startDate, hoverDate) ||
      isSameMonth(hoverDate, day));
    const isBackwardRange = !!endDate && !startDate &&
      (day.isBetween(hoverDate, endDate) ||
      isSameMonth(hoverDate, day));

    const isValidDayHovered = hoverDate && !this.isBlocked(hoverDate);

    return (isForwardRange || isBackwardRange) && isValidDayHovered;
  }

  isInSelectedSpan(day) {
    const { startDate, endDate } = this.props;
    return day.isBetween(startDate, endDate);
  }

  isLastInRange(day) {
    return this.isInSelectedSpan(day) && isNextMonth(day, this.props.endDate);
  }

  isBlocked(day) {
    const { isDayBlocked, isOutsideRange } = this.props;
    return isDayBlocked(day) || isOutsideRange(day) || this.doesNotMeetMinimumNights(day);
  }

  render() {
    const {
      isDayBlocked,
      isDayHighlighted,
      isOutsideRange,
      numberOfYears,
      orientation,
      monthFormat,
      navPrev,
      navNext,
      onOutsideClick,
      onPrevClick,
      onNextClick,
      withPortal,
      enableOutsideDays,
      initialVisibleYear,
      focusedInput,
      renderMonth,

      startDate,
      endDate
    } = this.props;

    const modifiers = {
      today: day => isSameMonth(day, this.today),
      blocked: day => this.isBlocked(day),
      'blocked-calendar': isDayBlocked,
      'blocked-out-of-range': isOutsideRange,
      'blocked-minimum-nights': day => this.doesNotMeetMinimumNights(day),
      'highlighted-calendar': isDayHighlighted,
      valid: day => !this.isBlocked(day),
      // before anything has been set or after both are set
      hovered: day => this.isHovered(day),

      // while start date has been set, but end date has not been
      'hovered-span': day => this.isInHoveredSpan(day),
      'after-hovered-start': day => this.isMonthAfterHoveredStartDate(day),
      'last-in-range': day => this.isLastInRange(day),

      // once a start date and end date have been set
      'selected-start': day => isSameMonth(day, startDate),
      'selected-end': day => isSameMonth(day, endDate),
      'selected-span': day => this.isInSelectedSpan(day),
    };

    return (
      <MonthPicker
        ref={(ref) => { this.dayPicker = ref; }}
        isFocused={this.props.isFocused}
        orientation={orientation}
        enableOutsideDays={enableOutsideDays}
        modifiers={modifiers}
        numberOfYears={numberOfYears}
        onMonthClick={this.onMonthClick}
        onMonthMouseEnter={this.onMonthMouseEnter}
        onMonthMouseLeave={this.onMonthMouseLeave}
        onPrevClick={onPrevClick}
        onNextClick={onNextClick}
        monthFormat={monthFormat}
        withPortal={withPortal}
        hidden={!focusedInput}
        initialVisibleYear={initialVisibleYear}
        onOutsideClick={onOutsideClick}
        navPrev={navPrev}
        navNext={navNext}
        renderMonth={renderMonth}
      />
    );
  }
}

MonthPickerRangeController.propTypes = propTypes;
MonthPickerRangeController.defaultProps = defaultProps;
